import { useCallback, useEffect, useState } from "react";
import { actualizar, rpc } from "../../services/datos";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";

const OPCIONES_SEMANAS = [4, 8, 12];

export function textoAusencia(miembro) {
  if (!miembro.ultima_asistencia) return "Sin asistencias registradas";
  const semanas = Math.floor(miembro.dias_sin_asistir / 7);
  return semanas >= 1 ? `Hace ${semanas} semana(s)` : `Hace ${miembro.dias_sin_asistir} día(s)`;
}

// Revisión de permanencia: miembros que llevan varias semanas sin asistir.
// Cuenta la asistencia a servicios y los turnos de servicio confirmados.
function Permanencia() {
  const [semanas, setSemanas] = useState(4);
  const [datos, setDatos] = useState(null);
  const [verTodos, setVerTodos] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const cargar = useCallback(() => {
    rpc("permanencia", { p_semanas: semanas }).then(setDatos).catch((e) => setMensaje(e.message));
  }, [semanas]);

  useEffect(() => { cargar(); }, [cargar]);

  const marcarInactivo = async (miembro) => {
    if (!(await confirmar(`¿Marcar a ${miembro.nombres} ${miembro.apellidos} como inactivo?`, { aceptar: "Marcar inactivo", peligro: false }))) return;
    try {
      await actualizar("miembros", miembro.id, { estado: "inactivo" });
      setMensaje(`${miembro.nombres} quedó como inactivo. Si dejó la iglesia, registra la baja en el módulo Bajas.`);
      cargar();
    } catch (error) {
      setMensaje(error.message);
    }
  };

  const lista = datos ? datos.miembros.filter((m) => verTodos || m.inactivo) : [];

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <h2 className="titulo-pagina">Permanencia</h2>
          <p className="text-gray-400 text-sm mt-1">Miembros que llevan varias semanas sin asistir a un servicio ni servir en un turno.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-gray-400 text-sm">
            Sin asistir desde hace
            <Selector value={semanas} onChange={(e) => setSemanas(Number(e.target.value))}
              className="ml-2 campo w-auto">
              {OPCIONES_SEMANAS.map((s) => <option key={s} value={s}>{s} semanas</option>)}
            </Selector>
          </label>
          <label className="text-gray-400 text-sm flex items-center gap-2">
            <input type="checkbox" checked={verTodos} onChange={(e) => setVerTodos(e.target.checked)} className="accent-yellow-500" />
            Ver todos
          </label>
        </div>
      </div>

      {mensaje && <p className="mb-4 text-sky-200 text-sm">{mensaje}</p>}

      {!datos ? (
        <Esqueleto />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl border border-yellow-800 bg-yellow-950/30 p-4">
              <p className="text-yellow-300 text-xs uppercase">Para revisar</p>
              <p className="text-yellow-100 text-2xl font-bold mt-1">{datos.inactivos}</p>
            </div>
            <div className="panel-interior p-4">
              <p className="text-gray-400 text-xs uppercase">Miembros</p>
              <p className="text-white text-2xl font-bold mt-1">{datos.miembros.length}</p>
            </div>
          </div>
          {!datos.hay_registros_de_asistencia && (
            <p className="panel-interior mb-4 p-4 text-sm text-gray-300">
              Aún no hay asistencias registradas. Registra la asistencia de cada servicio para que esta revisión sea precisa.
            </p>
          )}
          {lista.length === 0 ? (
            <p className="text-gray-400">No hay miembros para revisar con este plazo.</p>
          ) : (
            <div className="space-y-2">
              {lista.map((m) => (
                <article key={m.id} className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${m.inactivo ? "border-yellow-800 bg-yellow-950/20" : "border-gray-700 bg-medio"}`}>
                  <div>
                    <p className="text-white font-semibold">{m.nombres} {m.apellidos}</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {textoAusencia(m)} · Estado: {m.estado || "sin estado"}
                    </p>
                  </div>
                  {m.inactivo && (
                    <button onClick={() => marcarInactivo(m)}
                      className="rounded-lg border border-yellow-700 px-3 py-1.5 text-sm text-yellow-300 hover:bg-yellow-900 transition">
                      Marcar inactivo
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Permanencia;
